/**
 * Redaction helpers used by the shared HTTP client's telemetry interceptor
 * to ensure secrets and sensitive payloads never reach logs/telemetry.
 */

const SAFE_QUERY_PARAM_ALLOWLIST = new Set([
	'page',
	'limit',
	'offset',
	'pagesize',
	'perpage',
	'sort',
	'order',
	'orderby',
	'lang',
	'locale',
	'format',
	'type',
	'version',
	'includecount',
])

function tokenize(name: string): string[] {
	return name
		.replace(/(?<lower>[a-z\d])(?<upper>[A-Z])/g, '$<lower>-$<upper>')
		.toLowerCase()
		.split(/[^a-z\d]+/)
		.filter(Boolean)
}

function isSafeQueryParam(name: string): boolean {
	const normalized = tokenize(name).join('')
	return SAFE_QUERY_PARAM_ALLOWLIST.has(normalized)
}

const SENSITIVE_HEADER_NAMES = new Set(
	[
		'authorization',
		'cookie',
		'set-cookie',
		'x-reg-token',
		'ocp-apim-subscription-key',
		'x-api-key',
	].map((name) => name.toLowerCase())
)

const REDACTED = '***'

const SECRET_ENV_NAME_PATTERN =
	/(?:SECRET|API_KEY|KEY$|_KEY_|TOKEN|PASS|CLIENTSECRET|CLIENT_SECRET|REPO_KEY|SUBSCRIPTIONKEY)/i

let cachedSecretValues: string[] | undefined

function getSecretEnvValues(): string[] {
	if (cachedSecretValues !== undefined) return cachedSecretValues
	const values: string[] = []
	for (const [name, value] of Object.entries(process.env)) {
		if (!value) continue
		if (name.startsWith('NEXT_PUBLIC_')) continue
		if (!SECRET_ENV_NAME_PATTERN.test(name)) continue
		if (value.length < 6) continue
		values.push(value)
	}
	cachedSecretValues = values
	return values
}

export function __resetRedactCacheForTests(): void {
	cachedSecretValues = undefined
}

function redactSecretsInUrl(url: string): string {
	const secrets = getSecretEnvValues()
	if (secrets.length === 0) return url
	let result = url
	for (const secret of secrets) {
		if (result.includes(secret)) {
			// Split/join avoids needing to escape regex metacharacters.
			result = result.split(secret).join(REDACTED)
		}
	}
	return result
}

/**
 * Redacts query-string parameter values from a URL using a fail-closed
 * allowlist: only parameter names known to be safe (pagination, sort,
 * locale, etc. -- see SAFE_QUERY_PARAM_ALLOWLIST) keep their values. Every
 * other parameter -- including ones that carry emails, user IDs,
 * coordinates, postcodes, or any other personal data -- is masked. Leaves
 * the origin and path untouched. If the URL cannot be parsed (e.g.
 * malformed), the entire query string is stripped rather than returning
 * the input unchanged -- a value we can't parse is a value we can't
 * selectively redact, so the fail-closed default is to drop it entirely.
 */
export function redactUrl(url: string): string {
	if (!url) return url
	const isRelative = !/^[a-z][a-z\d+\-.]*:\/\//i.test(url)
	const base = 'https://redact-base.invalid'

	try {
		const parsed = new URL(url, isRelative ? base : undefined)
		for (const key of parsed.searchParams.keys()) {
			if (!isSafeQueryParam(key)) {
				parsed.searchParams.set(key, REDACTED)
			}
		}
		const result = parsed.toString()
		const withoutBase = isRelative ? result.replace(base, '') : result
		return redactSecretsInUrl(withoutBase)
	} catch {
		const queryIndex = url.indexOf('?')
		const stripped = queryIndex === -1 ? url : url.slice(0, queryIndex)
		return redactSecretsInUrl(stripped)
	}
}

/**
 * Returns a copy of the given headers with sensitive values masked.
 * Accepts any header-like object (AxiosHeaders, plain objects, etc.).
 */
export function redactHeaders(
	headers: Record<string, unknown> | undefined
): Record<string, unknown> {
	if (!headers) return {}

	const redacted: Record<string, unknown> = {}
	for (const [key, value] of Object.entries(headers)) {
		redacted[key] = SENSITIVE_HEADER_NAMES.has(key.toLowerCase())
			? REDACTED
			: value
	}
	return redacted
}
